import { useEffect, useRef, useState } from 'react';

/**
 * @file THE STATION IDENT — who is on air, and what is playing. It appears when
 * a track starts, holds for a few seconds and fades out.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE STATION NAME IS THE REQUIREMENT, AND IT IS PRINTED VERBATIM
 * ─────────────────────────────────────────────────────────────────────────────
 * The owner's spec is that the station is NAMED every time a track plays —
 * that is why the content in `src/game/radio.js` is grouped by station rather
 * than being one flat playlist, and why `'radio:track'` carries `stationName` at
 * all. So the card LEADS with the station and puts the track title under it, and
 * the string comes off the event: there is no `'Lush Drive'` anywhere in this
 * file, and there must never be one. The entire data model exists so that a
 * second station is a data edit and nothing else, and one hard-coded name here
 * would quietly make it a code change instead.
 *
 * ⚠ A PAYLOAD WITH NO `stationName` DRAWS NOTHING, rather than falling back to a
 * title on its own. A card that names the song but not the station is precisely
 * the thing the feature was asked for and would look like it was working.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT SUBSCRIBES DIRECTLY, AND THE ARRIVAL IS THE EVENT — NOT THE VALUE
 * ─────────────────────────────────────────────────────────────────────────────
 * `useEngineBus` coalesces a channel into a VALUE, and re-shows nothing when the
 * value is unchanged. That is right for `'player:cash'` and wrong here twice
 * over: the same track can legitimately be announced again (step out, step back
 * in and the resume re-announces it), and a model that hands out a reused
 * scratch object — the `'hud:blips'` idiom this codebase already uses in two
 * places — would deliver an identical REFERENCE and never re-render at all. So
 * this holds its own subscription and stamps each arrival with a sequence
 * number, which is also what re-triggers the entrance animation below.
 *
 * ⚠ `'radio:track'` FIRES ON TRANSITIONS ONLY (`systems/Radio.js` is its sole
 * publisher), so there is no throttle here and none is wanted: this is a handful
 * of events an hour, and a throttle could only ever swallow one of them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE ENTRANCE IS AN ANIMATION AND THE EXIT IS A TRANSITION, ON TWO ELEMENTS
 * ─────────────────────────────────────────────────────────────────────────────
 * `.hud-fade-in` is declared `both`, so after it finishes it KEEPS APPLYING its
 * final keyframe — and a CSS animation beats an ordinary declaration. Put the
 * fade-out `opacity-0` on the same element and it simply never takes effect: the
 * card would pop out of existence at the end of the hold with no fade at all,
 * which is exactly the artifact this readout exists to avoid. Hence two nodes:
 * the outer one owns the OPACITY TRANSITION, the inner one owns the animation
 * and is keyed on the sequence number so a second track re-plays it. Their
 * opacities multiply, so the outer's fade dims a fully-arrived card.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE IT SITS
 * ─────────────────────────────────────────────────────────────────────────────
 * TOP-CENTRE, docked UNDER the `TimeOfDay` pill (`top-3`, ~1.8 rem tall) with
 * the same ~0.9 rem of clearance the bottom-centre ladder uses between its three
 * lines. Every other corner is spoken for and none of them would have been
 * right: the bottom-left column (phone, ammo, health, minimap) and the top-right
 * cluster (wanted, cash) are the readings taken MID-FIGHT and must not acquire a
 * fourth thing that comes and goes, and the bottom-centre ladder
 * (`MissionArrow` → `PhoneRing` → `Prompt`) is the ACTIONABLE lane — turn here,
 * answer this, press F. A station ident is the opposite of all of those: it is
 * transient, it asks for nothing, and it belongs beside the clock, which is the
 * other thing on this screen that simply states where you are.
 *
 * ⚠ NO z-INDEX, deliberately, exactly like `Prompt` and `PhoneRing`: this
 * belongs UNDER `FadeOverlay` (z-30), so driving into a shop takes it with the
 * rest of the screen instead of leaving a station name over a black frame. It
 * is `pointer-events-none` in every state and opts nothing back in — there is
 * nothing to press on it, and the dial does not exist yet.
 *
 * The one overlap it can have is with `TimeOfDay` EXPANDED, which is a panel the
 * player opens deliberately and a control they are not driving through.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** How long the card stays up at full strength, ms. */
const HOLD_MS = 4500;
/** The fade out, ms. Must match the `duration-500` below — one of them is CSS
 *  and the other is a timer, so they cannot be derived from each other; the
 *  timer is what unmounts, so it may only ever be the LONGER of the two. */
const FADE_MS = 600;

/** @param {any} v @returns {string} A printable string, or ''. */
const text = (v) => (typeof v === 'string' ? v.trim() : '');

export default function RadioHud({ engine }) {
  /** @type {[{seq: number, station: string, title: string}|null, Function]} */
  const [card, setCard] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const seq = useRef(0);

  // ── the subscription ───────────────────────────────────────────────────────
  useEffect(() => {
    setCard(null);
    setLeaving(false);
    const bus = engine?.bus;
    if (!bus?.on) return undefined;
    // Named, so the `off(type, fn)` fallback has something to name — `on()`
    // returns its own unsubscriber on `createBus()`, and this is `GameAudio`'s
    // `sub()` guard in a component.
    const onTrack = (t) => {
      const station = text(t?.stationName);
      if (!station) return; // see the header — no station, no card
      seq.current += 1;
      setLeaving(false);
      setCard({ seq: seq.current, station, title: text(t?.title) });
    };
    const off = bus.on('radio:track', onTrack);
    return () => {
      if (typeof off === 'function') off();
      else bus.off?.('radio:track', onTrack);
    };
  }, [engine]);

  // ── hold, fade, unmount ────────────────────────────────────────────────────
  // Keyed on the whole card object, so a second track arriving mid-fade cancels
  // both timers through the cleanup and starts its own pair. The unmount is a
  // timer rather than a `transitionend` listener on purpose: `transitionend`
  // never fires for a transition the browser skipped (a background tab, or
  // `prefers-reduced-motion` below), and a card that never unmounts is a card
  // that never shows again.
  useEffect(() => {
    if (!card) return undefined;
    const hold = setTimeout(() => setLeaving(true), HOLD_MS);
    const gone = setTimeout(() => {
      setCard(null);
      setLeaving(false);
    }, HOLD_MS + FADE_MS);
    return () => {
      clearTimeout(hold);
      clearTimeout(gone);
    };
  }, [card]);

  if (!card) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 top-14 flex select-none justify-center px-4">
      {/* THE FADE LAYER. See the header: it may not be the same element as the
          entrance animation, or the animation's `both` fill wins and there is no
          fade. `motion-reduce` drops it to a straight cut, which is what the
          rest of the HUD does with `.hud-fade-in` under the same query. */}
      <div
        className={`transition-opacity duration-500 motion-reduce:transition-none ${
          leaving ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {/* ⚠ `role="status"`, and here that IS right — unlike `MissionArrow`'s
            distance, this is one short announcement per track and reading it
            aloud is exactly what a screen reader should do with it. */}
        <div
          key={card.seq}
          role="status"
          className="hud-fade-in flex max-w-[22rem] items-center gap-3 rounded-[3px] border border-neon/50 bg-void/85 px-3.5 py-2 backdrop-blur-sm"
        >
          <WaveGlyph />

          <span className="flex min-w-0 flex-col">
            {/* THE STATION LEADS. Neon cyan is the palette's THIRD accent and
                the only one still unspent in the playing HUD — gold has always
                meant money here and siren red means heat, so a radio painted in
                either would be read as one of those at a glance. It is not a
                fourth colour: `styles.css` authorises exactly three. */}
            <span className="truncate font-display text-[15px] uppercase leading-none tracking-[0.06em] text-neon">
              {card.station}
            </span>
            {card.title ? (
              <span className="mt-1 truncate font-hud text-[12px] leading-none tracking-[0.04em] text-hud-dim">
                {card.title}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Three arcs off a point — a transmitter, not a speaker. Authored at 18 px to
 * match the objective arrow's dial, and `aria-hidden` because the row beside it
 * already says everything it means.
 */
function WaveGlyph() {
  return (
    <span aria-hidden="true" className="block h-[18px] w-[18px] shrink-0 text-neon">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="18" r="1.6" fill="currentColor" stroke="none" />
        <path d="M8.6 14.6a4.8 4.8 0 0 1 6.8 0" />
        <path d="M5.9 11.4a8.6 8.6 0 0 1 12.2 0" />
        <path d="M3.4 8.2a12.2 12.2 0 0 1 17.2 0" />
      </svg>
    </span>
  );
}
